import { memo } from "react";
import {
  BarChart3,
  Bookmark,
  Brain,
  Clock,
  FileText,
  Flame,
  GraduationCap,
  Lightbulb,
  Navigation,
  Play,
  Smartphone,
  Sparkles,
  StickyNote,
  TrendingUp,
  Award,
  Activity,
  CheckCircle2,
} from "lucide-react";

export type MiniMockupVariant =
  | "progress"
  | "navigation"
  | "timeTracking"
  | "notes"
  | "bookmarks"
  | "videoFeatures"
  | "flashcards"
  | "quizzes"
  | "resources"
  | "studyAnalytics"
  | "detailedAnalytics"
  | "streaks"
  | "certificates"
  | "insights"
  | "mobile";

interface Props {
  variant: MiniMockupVariant;
  /**
   * `popup` — absolute-positioned, fills its parent (used inside FeatureDetailDialog hero band).
   * `inline` — relative, self-sized; designed to sit inline above a card title.
   */
  size?: "popup" | "inline";
}

/**
 * Renders only the *inner* content of each variant. The outer positioning wrapper
 * is added by the top-level component based on `size`.
 */
const renderVariant = (variant: MiniMockupVariant) => {
  switch (variant) {
    case "progress":
      return (
        <div className="w-full max-w-sm space-y-2 rounded-xl border glass-border bg-card/60 backdrop-blur-md p-3 shadow-glass">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Curso ativo</span>
            <span className="text-[10px] font-bold text-primary">68%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted/40">
            <div className="h-full w-[68%] rounded-full bg-gradient-to-r from-primary to-accent" />
          </div>
          <div className="flex items-end gap-0.5 pt-1.5">
            {[24, 38, 30, 52, 44, 60, 72].map((h, i) => (
              <div key={i} className="flex-1 rounded-sm bg-primary/60" style={{ height: `${h * 0.4}px` }} />
            ))}
          </div>
        </div>
      );

    case "navigation":
      return (
        <div className="w-full max-w-sm space-y-1.5 rounded-xl border glass-border bg-card/60 backdrop-blur-md p-3 shadow-glass">
          {[
            { label: "Módulo 1 · Introdução", active: false, done: true },
            { label: "Módulo 2 · Fundamentos", active: true, done: false },
            { label: "Módulo 3 · Prática", active: false, done: false },
          ].map((row, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[10px] ${
                row.active ? "bg-primary/15 text-foreground" : "text-muted-foreground"
              }`}
            >
              {row.done ? (
                <CheckCircle2 className="h-3 w-3 text-primary" />
              ) : (
                <Navigation className="h-3 w-3" />
              )}
              <span>{row.label}</span>
              {row.active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />}
            </div>
          ))}
        </div>
      );

    case "timeTracking":
      return (
        <div className="grid w-full max-w-sm grid-cols-7 gap-1 rounded-xl border glass-border bg-card/60 backdrop-blur-md p-3 shadow-glass">
          {Array.from({ length: 7 * 4 }).map((_, i) => {
            const intensity = [0.1, 0.25, 0.5, 0.7, 1][i % 5];
            return (
              <div
                key={i}
                className="aspect-square rounded-sm"
                style={{ background: `hsl(var(--primary) / ${intensity})` }}
              />
            );
          })}
          <div className="col-span-7 mt-1 flex items-center gap-1.5 text-[9px] text-muted-foreground">
            <Clock className="h-2.5 w-2.5" />
            <span>4h 12min esta semana</span>
          </div>
        </div>
      );

    case "notes":
      return (
        <div className="w-full max-w-sm space-y-2 rounded-xl border glass-border bg-card/60 backdrop-blur-md p-3 shadow-glass">
          {[
            "Conceitos-chave do módulo 2…",
            "Lembrar de revisar a fórmula…",
            "Exemplo prático: ver minuto 12:34",
          ].map((text, i) => (
            <div key={i} className="flex items-start gap-2">
              <StickyNote className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
              <div className="flex-1 space-y-0.5">
                <div className="text-[10px] text-foreground/90">{text}</div>
                <div className="h-1 w-3/4 rounded-full bg-muted/40" />
              </div>
            </div>
          ))}
          <div className="flex gap-1 pt-1">
            <span className="rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[9px] text-primary">#módulo2</span>
            <span className="rounded-full border border-accent/30 bg-accent/10 px-1.5 py-0.5 text-[9px] text-accent-foreground">#revisar</span>
          </div>
        </div>
      );

    case "bookmarks":
      return (
        <div className="w-full max-w-sm space-y-1.5 rounded-xl border glass-border bg-card/60 backdrop-blur-md p-3 shadow-glass">
          {["Aula 04 · Fundamentos", "Aula 09 · Caso prático", "Aula 12 · Revisão"].map((label, i) => (
            <div key={i} className="flex items-center gap-2 rounded-md bg-background/40 px-2 py-1.5 text-[10px]">
              <Bookmark className="h-3 w-3 fill-primary text-primary" />
              <span className="text-foreground/90">{label}</span>
              <Play className="ml-auto h-2.5 w-2.5 text-muted-foreground" />
            </div>
          ))}
        </div>
      );

    case "videoFeatures":
      return (
        <div className="w-full max-w-sm overflow-hidden rounded-xl border glass-border bg-card/60 backdrop-blur-md shadow-glass">
          <div className="relative flex h-16 items-center justify-center bg-gradient-to-br from-primary/20 to-accent/20">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-background/80 backdrop-blur-md">
              <Play className="h-4 w-4 fill-primary text-primary" />
            </div>
            <div className="absolute bottom-1 left-2 right-2 h-1 rounded-full bg-background/40">
              <div className="h-full w-2/5 rounded-full bg-primary" />
            </div>
          </div>
          <div className="space-y-1 p-2">
            {["00:00 · Introdução", "04:12 · Conceito central", "09:45 · Exemplo"].map((cap, i) => (
              <div key={i} className="flex items-center justify-between text-[10px]">
                <span className={i === 1 ? "text-primary" : "text-muted-foreground"}>{cap}</span>
                {i === 1 && <span className="text-[8px] text-primary">▶</span>}
              </div>
            ))}
          </div>
        </div>
      );

    case "flashcards":
      return (
        <div className="relative h-28 w-44">
          {[2, 1, 0].map((offset) => (
            <div
              key={offset}
              className="absolute inset-0 rounded-xl border glass-border bg-card/70 backdrop-blur-md shadow-glass"
              style={{
                transform: `translate(${offset * 6}px, ${offset * -4}px) rotate(${offset * 2}deg)`,
                opacity: 1 - offset * 0.2,
              }}
            >
              {offset === 0 && (
                <div className="flex h-full flex-col items-center justify-center gap-1 px-3 text-center">
                  <Brain className="h-4 w-4 text-primary" />
                  <div className="text-[10px] font-semibold text-foreground">O que é…?</div>
                  <div className="text-[9px] text-muted-foreground">Toque para virar</div>
                </div>
              )}
            </div>
          ))}
        </div>
      );

    case "quizzes":
      return (
        <div className="w-full max-w-sm space-y-2 rounded-xl border glass-border bg-card/60 backdrop-blur-md p-3 shadow-glass">
          <div className="flex items-center gap-1.5">
            <GraduationCap className="h-3 w-3 text-primary" />
            <span className="text-[10px] font-medium text-foreground/90">Pergunta 3 de 10</span>
          </div>
          <div className="space-y-1.5">
            {[
              { label: "Opção A", state: "wrong" },
              { label: "Opção B", state: "right" },
              { label: "Opção C", state: "idle" },
              { label: "Opção D", state: "idle" },
            ].map((opt, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-[10px] ${
                  opt.state === "right"
                    ? "border-primary/50 bg-primary/15 text-primary"
                    : opt.state === "wrong"
                      ? "border-destructive/40 bg-destructive/10 text-destructive"
                      : "glass-border bg-background/30 text-muted-foreground"
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${opt.state === "right" ? "bg-primary" : opt.state === "wrong" ? "bg-destructive" : "bg-muted"}`} />
                {opt.label}
              </div>
            ))}
          </div>
        </div>
      );

    case "resources":
      return (
        <div className="w-full max-w-sm space-y-1.5 rounded-xl border glass-border bg-card/60 backdrop-blur-md p-3 shadow-glass">
          {[
            { name: "manual-prático.pdf", size: "2.4 MB" },
            { name: "exercícios.docx", size: "612 KB" },
            { name: "slides-aula.pdf", size: "8.1 MB" },
          ].map((file, i) => (
            <div key={i} className="flex items-center gap-2 rounded-md bg-background/40 px-2 py-1.5 text-[10px]">
              <FileText className="h-3 w-3 text-primary" />
              <span className="text-foreground/90">{file.name}</span>
              <span className="ml-auto text-muted-foreground">{file.size}</span>
            </div>
          ))}
        </div>
      );

    case "studyAnalytics":
      return (
        <div className="w-full max-w-sm space-y-2 rounded-xl border glass-border bg-card/60 backdrop-blur-md p-3 shadow-glass">
          <div className="flex items-center gap-1.5">
            <Activity className="h-3 w-3 text-primary" />
            <span className="text-[10px] font-medium text-foreground/90">Padrão de sessão</span>
          </div>
          <div className="flex items-end gap-1 h-12">
            {[40, 65, 30, 80, 55, 90, 70, 45, 60].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm bg-gradient-to-t from-primary/40 to-accent/60"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
          <div className="flex justify-between text-[9px] text-muted-foreground">
            <span>Seg</span><span>Ter</span><span>Qua</span><span>Qui</span><span>Sex</span><span>Sáb</span><span>Dom</span>
          </div>
        </div>
      );

    case "detailedAnalytics":
      return (
        <div className="grid w-full max-w-sm grid-cols-3 gap-2">
          {[
            { label: "Conclusão", value: "82%", icon: TrendingUp },
            { label: "Tempo", value: "47h", icon: Clock },
            { label: "Sequência", value: "12d", icon: Flame },
          ].map((stat, i) => (
            <div key={i} className="rounded-xl border glass-border bg-card/60 backdrop-blur-md p-2.5 shadow-glass">
              <stat.icon className="mb-1 h-3 w-3 text-primary" />
              <div className="text-sm font-bold text-foreground">{stat.value}</div>
              <div className="text-[9px] text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>
      );

    case "streaks":
      return (
        <div className="w-full max-w-sm rounded-xl border glass-border bg-card/60 backdrop-blur-md p-3 shadow-glass">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Flame className="h-4 w-4 text-primary" />
              <span className="text-xs font-bold text-foreground">12 dias seguidos</span>
            </div>
            <span className="rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[9px] text-primary">Meta: 30</span>
          </div>
          <div className="flex justify-between gap-1">
            {Array.from({ length: 7 }).map((_, i) => (
              <div
                key={i}
                className={`flex h-6 flex-1 items-center justify-center rounded-md text-[9px] font-medium ${
                  i < 5 ? "bg-primary/30 text-primary" : "bg-background/30 text-muted-foreground"
                }`}
              >
                {["S", "T", "Q", "Q", "S", "S", "D"][i]}
              </div>
            ))}
          </div>
        </div>
      );

    case "certificates":
      return (
        <div className="relative w-full max-w-xs rounded-xl border border-primary/30 bg-card/70 backdrop-blur-md p-4 text-center shadow-glass">
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full border border-primary/40 bg-background/90 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-primary">
            Certificado
          </div>
          <Award className="mx-auto mb-1.5 h-7 w-7 text-primary" />
          <div className="font-serif italic text-xs text-foreground">Conclusão de Curso</div>
          <div className="mt-1 text-[9px] text-muted-foreground">Outorgado em abril de 2026</div>
          <div className="mt-2 flex justify-center gap-0.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <span key={i} className="h-1 w-3 rounded-full bg-gradient-to-r from-primary to-accent" />
            ))}
          </div>
        </div>
      );

    case "insights":
      return (
        <div className="w-full max-w-sm space-y-2 rounded-xl border glass-border bg-card/60 backdrop-blur-md p-3 shadow-glass">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-primary" />
            <span className="text-[10px] font-medium uppercase tracking-wider text-primary/80">IA recomenda</span>
          </div>
          {[
            "Revisar Módulo 2 esta semana",
            "Conclusão prevista: 12 maio",
            "Próximo melhor passo: Quiz 3",
          ].map((line, i) => (
            <div key={i} className="flex items-start gap-2">
              <Lightbulb className="mt-0.5 h-3 w-3 shrink-0 text-accent-foreground" />
              <span className="text-[10px] text-foreground/90">{line}</span>
            </div>
          ))}
        </div>
      );

    case "mobile":
      return (
        <div className="relative flex h-[170px] w-[100px] flex-col rounded-[18px] border-2 [border-color:hsl(var(--glass-tint)/0.20)] bg-card/70 p-1.5 shadow-glass backdrop-blur-md">
          {/* Notch */}
          <div className="mx-auto h-1 w-8 rounded-full [background-color:hsl(var(--glass-tint)/0.20)] mb-1" />
          {/* Screen */}
          <div className="flex flex-1 flex-col gap-1.5 rounded-[12px] bg-background/80 p-1.5">
            <div className="flex items-center gap-1">
              <Smartphone className="h-2.5 w-2.5 text-primary" />
              <div className="h-1.5 w-10 rounded-full bg-foreground/30" />
            </div>
            {/* Course rows */}
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="flex items-center gap-1.5 rounded-md border glass-border bg-card/60 px-1.5 py-1"
              >
                <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-primary/20">
                  <Play className="h-2 w-2 text-primary" />
                </div>
                <div className="flex flex-1 flex-col gap-0.5">
                  <div className="h-1 w-full rounded-full bg-foreground/25" />
                  <div className="h-0.5 w-3/5 rounded-full bg-primary/50" />
                </div>
              </div>
            ))}
            {/* Bottom tab bar */}
            <div className="mt-auto flex items-center justify-around rounded-md bg-card/80 px-1 py-1">
              <div className="h-1 w-1 rounded-full bg-primary" />
              <div className="h-1 w-1 rounded-full bg-foreground/30" />
              <div className="h-1 w-1 rounded-full bg-foreground/30" />
            </div>
          </div>
        </div>
      );

    default:
      return <BarChart3 className="h-12 w-12 text-primary" />;
  }
};

/**
 * Stylised "component representations" used both inside the FeatureDetailDialog
 * hero band (size="popup") and inline above feature cards (size="inline").
 *
 * Built from real UI primitives (rounded cards, chips, bars, list rows) so they
 * read as honest previews of the actual app. All visuals use semantic design
 * tokens so they adapt to theme automatically.
 */
export const FeatureMiniMockup = memo(({ variant, size = "popup" }: Props) => {
  const content = renderVariant(variant);

  if (size === "inline") {
    // Inline: relative, fills width of parent band, vertically centered.
    return (
      <div className="relative flex h-full w-full items-center justify-center px-3 py-2 [&>div]:scale-[0.85] sm:[&>div]:scale-90">
        {content}
      </div>
    );
  }

  // Popup (default): absolute fill of dialog hero band.
  return (
    <div className="absolute inset-0 flex items-center justify-center px-6">
      {content}
    </div>
  );
});

FeatureMiniMockup.displayName = "FeatureMiniMockup";
